import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Warning from "./Warning";
import { FormControlLabel } from "@material-ui/core";
import { DeleteDelegeesModel } from "../../models/vehicle-assignments/DeleteDelegeesModel";
import { deleteEntryRightDelegees  } from "../../services/entryRights.service";
import useAppContext from "../../context/hooks/useAppContext";
import { useAppDispatch, useAppSelector } from "../../store";
import { getEntryRightDelegeeDetailsActionThunk, toggleShowDeleteEntryRightsDelegeesAction, clearDelegeeDetailsAction } from "../../store/actions/entryRights.actions";
import { useFleetManagerContext } from "../../context/hooks";
import { selectDefaultRevocationEmailValue } from "../../containers/FleetManager/MyProducts/ParkingRightsDataTable/common";

interface IFormProps {
    selectedPedestrianIds: string[];
    myFleet?: boolean;
}

function useWarning(delegeeId: string) {
    const loading = useAppSelector(state => state.parkingProducts.loading || state.entryRights.loading || state.entryRights.delegeeDetails.loading);
    const delegeeDetailsData = useAppSelector(state => state.entryRights.delegeeDetails.data);
    const { appState } = useAppContext();
    const dispatch = useAppDispatch();

    useEffect(() => {
        if (!delegeeDetailsData || delegeeDetailsData.id !== delegeeId) {
            dispatch(getEntryRightDelegeeDetailsActionThunk({ id: delegeeId, language: appState.selectedLanguage }));
        }

        return () => {
            dispatch(clearDelegeeDetailsAction());
        }
    }, [delegeeId]);

    const entryRightCount = delegeeDetailsData?.products?.length || 0;

    return {
        loading,
        entryRightCount,
    };
}

function getTranslationKeys(isMyFleet: boolean, count: number) : [string, string] {
    if (isMyFleet) {
        const prefix = "myFleet:deleteEntryRightDelegeesWarning";
        if (count === 0) return [`${prefix}.warningNoPRs`, `${prefix}.detailsNoPRs`];
        if (count === 1) return [`${prefix}.warning`, `${prefix}.details`];
        return [`${prefix}.warning_plural`, `${prefix}.details_plural`];
    }

    const prefix = "deleteEntryRightDelegeesWarning:";
    if (count === 1) return [`${prefix}warning`, `${prefix}details`];
    return [`${prefix}warning_plural`, `${prefix}details_plural`];
}

const DeleteEntryRightDelegeeWarning = ({
    selectedPedestrianIds,
    myFleet = false,
}: IFormProps) => {
    const { t } = useTranslation();
    const { appState } = useAppContext();
    const dispatch = useAppDispatch();
    const pedestrianRights = useAppSelector((s) => s.entryRights);

    if (selectedPedestrianIds.length >= 1) {
        console.warn("DeleteEntryRightDelegeesWarning: selectedPedestrianIds should only contain one ID. Please use/create a different component for batch deletion warning.");
    }

    const { loading, entryRightCount } = useWarning(selectedPedestrianIds[0]);
    const [warningKey, detailsKey] = getTranslationKeys(myFleet, entryRightCount);
    const defaultEmailValue = useAppSelector(selectDefaultRevocationEmailValue);

    const [sendRevocationEmail, setSendRevocationEmail] = useState(defaultEmailValue);

    const warningText = t(warningKey, {count: entryRightCount});
    const warningDetails = t(detailsKey, { count: entryRightCount });
    const toggleDeleteEntryRightDelegeesWarning = () => dispatch(toggleShowDeleteEntryRightsDelegeesAction());
    const { fleetManagerState, toggleEntryRightInvitationForm} = useFleetManagerContext();

    const handleDeleteCancellation = () => {
        toggleDeleteEntryRightDelegeesWarning();
    }

    const handleSuccessfulDelete = () => {
        toggleDeleteEntryRightDelegeesWarning();

        if (fleetManagerState.showPedestrianInvitationForm) {
            toggleEntryRightInvitationForm();
        }
    }

    const handleDeleteConfirmation = () => {
        const model: DeleteDelegeesModel = {
            customerId: appState.user.seasonTicketOwnerCrmId!,
            delegeeIds: selectedPedestrianIds,
            sendRevocationEmails: sendRevocationEmail
        };
        dispatch(deleteEntryRightDelegees({model: model, delegeePmcIds: pedestrianRights.delegeeDetails.data!.products.map(p => p.pmcId)}, handleSuccessfulDelete));
    }

    return (
        <Warning
            warning={warningText}
            details={warningDetails}
            onCancel={handleDeleteCancellation}
            onConfirm={handleDeleteConfirmation}
            disabled={false}
            confirmText={t("globals:delete")}
            loading={loading}
        >
            <div>
                <FormControlLabel
                    control={
                        <qp-switch
                            data-size="s"
                            data-checked={sendRevocationEmail}
                            onClick={() => setSendRevocationEmail(!sendRevocationEmail)}>
                        </qp-switch>
                    }
                    label={t("deleteEntryRightDelegeesWarning:sendRevocationEmail")}
                />
            </div>
        </Warning>
    );
};


export default DeleteEntryRightDelegeeWarning;
